import React, { useRef, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { buttonStyle, iconStyle, contentEditorStyles } from './ContentEditorDropdown.style';
import { CONTENT_EDITOR_DROPDOWN_CONSTANTS } from './constant';
import { ContentEditorDropdownProps } from './types';

const ContentEditorDropdown: React.FC<ContentEditorDropdownProps> = ({
  open,
  menuItems,
  onToggle,
  onClose,
  onNavigate,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);

  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom, right: window.innerWidth - rect.right });
    } else {
      setMenuPosition(null);
    }
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideWrapper = wrapperRef.current?.contains(target);
      const insideMenu = menuRef.current?.contains(target);
      if (!insideWrapper && !insideMenu) {
        onClose();
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onClose]);

  return (
    <div style={contentEditorStyles.wrapper} ref={wrapperRef}>
      <button
        ref={buttonRef}
        style={buttonStyle}
        onClick={onToggle}
        aria-expanded={open}
      >
        <span style={contentEditorStyles.triggerLabel}>{CONTENT_EDITOR_DROPDOWN_CONSTANTS.LABEL}</span>
        <span style={contentEditorStyles.triggerIconWrap} aria-hidden="true">
          <svg
            style={{
              ...iconStyle,
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'none',
            }}
            viewBox="0 0 24 24"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
          >
            <path opacity=".87" fill="none" d="M24 24H0V0h24v24z"></path>
            <path d="M15.88 9.29L12 13.17 8.12 9.29c-.39-.39-1.02-.39-1.41 0-.39.39-.39 1.02 0 1.41l4.59 4.59c.39.39 1.02.39 1.41 0l4.59-4.59c.39-.39.39-1.02 0-1.41-.39-.38-1.03-.39-1.42 0z"></path>
          </svg>
        </span>
      </button>

      {open && menuPosition && ReactDOM.createPortal(
        <div
          ref={menuRef}
          style={{
            ...contentEditorStyles.dropdownMenuPortal,
            top: menuPosition.top,
            right: menuPosition.right,
          }}
        >
          <div style={contentEditorStyles.dropdownColumn}>
            <div style={contentEditorStyles.dropdownHeader}>
              <span style={contentEditorStyles.dropdownHeaderText}>
                {CONTENT_EDITOR_DROPDOWN_CONSTANTS.LABEL}
              </span>
            </div>
            <div style={contentEditorStyles.dropdownLinks}>
              {menuItems.map((item) => (
                <div key={item.key} style={contentEditorStyles.dropdownItem}>
                  <button
                    style={contentEditorStyles.dropdownLink}
                    onClick={() => onNavigate(item.path)}
                  >
                    <span
                      style={contentEditorStyles.dropdownItemText}
                      onMouseEnter={(e) => {
                        Object.assign(e.currentTarget.style, contentEditorStyles.dropdownItemTextHover);
                      }}
                      onMouseLeave={(e) => {
                        Object.assign(e.currentTarget.style, contentEditorStyles.dropdownItemTextDefault);
                      }}
                    >
                      {item.label}
                    </span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ContentEditorDropdown;